<!-- eslint-disable vue/no-mutating-props -->
<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import InputRadioGroup from './InputRadioGroupStylizedAsButton.vue';
import { useVuelidate } from '@vuelidate/core';
import { helpers } from '@vuelidate/validators';
import { extraAdds } from 'dashboard/routes/dashboard/conversation/contact/helpers/extraAdds.js';

export default {
  components: { InputRadioGroup },
  props: {
    inbox: {
      type: Object,
      default: () => {},
    },
    conversation: {
      type: Object,
      default: () => {},
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      cpf: '',
      isLoading: false,
      setLabelError: false,
      updateCpfError: false,
      selectedLabels: [],
      selectedResolutionOption: '',
      selectedResolutionReason: '',
      resolutionReasons: [],
      resolutionOptions: [
        {
          id: 'objective_achieved',
          title: this.$t('CONVERSATION.RESOLUTION_MODAL.OPTIONS.FIRST_LABEL'),
          checked: true,
        },
        {
          id: 'objective_not_achieved',
          title: this.$t('CONVERSATION.RESOLUTION_MODAL.OPTIONS.SECOND_LABEL'),
          checked: false,
        },
        {
          id: 'doubt',
          title: this.$t('CONVERSATION.RESOLUTION_MODAL.OPTIONS.THIRD_LABEL'),
          checked: false,
        },
        {
          id: 'other',
          title: this.$t('CONVERSATION.RESOLUTION_MODAL.OPTIONS.FOURTH_LABEL'),
          checked: false,
        },
      ],
    };
  },
  validations() {
    return {
      cpf: {
        isValidCPF(value) {
          if (!this.cpfIsMandatory) return true;
          const onFormat = helpers.regex(
            /^(\d{3}\.?\d{3}\.?\d{3}-?\d{2}|\d{11})$/,
            value
          );
          const valid = extraAdds.cpfUtils.isCPFValid(value);
          return onFormat && valid;
        },
      },
      selectedLabels: {
        isEmptyAndSetLabelMandatory() {
          if (this.setLabelIsMandatory && this.conversationHasLabel)
            return true;
          if (!this.setLabelIsMandatory) return true;
          return !!this.selectedLabels.length;
        },
      },
    };
  },
  computed: {
    ...mapGetters({
      audienceList: 'labels/getLabels',
      conversationUiFlags: 'conversationLabels/getUIFlags',
    }),
    cpfIsMandatory() {
      return this.inbox?.resolution_config?.options?.cpf_is_mandatory;
    },
    conversationHasLabel() {
      return this.conversation.labels && this.conversation.labels.length > 0;
    },
    setLabelIsMandatory() {
      return this.inbox?.resolution_config?.options?.set_label_is_mandatory;
    },
    contact() {
      return this.$store.getters['contacts/getContact'](
        this.conversation?.meta?.sender?.id
      );
    },
    resolveButtonDisabled() {
      return !this.selectedResolutionReason || this.v$.$invalid;
    },
  },
  watch: {
    selectedResolutionOption(newVal) {
      const reasons = this.inbox.conversation_resolution_reasons.filter(
        r => r.resolution_type === newVal
      );
      this.resolutionReasons = reasons;
    },
  },
  mounted() {
    this.setCpfIfAvailable();
  },
  methods: {
    onClose() {
      this.$emit('close');
    },
    onChangeResolutionOption(selectedOption) {
      this.selectedResolutionOption = selectedOption;
    },
    onChangeResolutionReason(selectedReasons) {
      if (!Number(selectedReasons)) return;
      this.selectedResolutionReason = selectedReasons;
    },
    setCpfIfAvailable() {
      const curentCpf =
        this.contact?.additional_attributes?.extraAdds?.cpf?.value;
      if (curentCpf) this.cpf = curentCpf;
    },
    async resolveConversationWithReason() {
      this.isLoading = true;
      await Promise.all([
        this.addLabelsToConversation(),
        this.addOrUpdateCPF(),
      ]);
      await this.resolveConversation();
      this.isLoading = false;
    },
    async addLabelsToConversation() {
      const labelsToAdd = this.selectedLabels.map(label => label.title);
      if (!labelsToAdd.length) return;

      const currentLabels = this.conversation?.labels;
      await this.$store.dispatch('conversationLabels/update', {
        conversationId: this.conversation.id,
        labels: [...new Set([...labelsToAdd, ...currentLabels])],
      });
      const badRequisition = this.conversationUiFlags?.isError;
      if (badRequisition) {
        useAlert(this.$t('CONVERSATION.RESOLUTION_MODAL.SET_LABEL.API_ERROR'));
      }
      this.setLabelError = badRequisition;
    },
    async addOrUpdateCPF() {
      // We only will show cpf input if its mandatory
      if (!this.cpfIsMandatory) return;

      if (this.v$.cpf.$invalid) this.updateCpfError = true;
      else {
        const additAttribUpdated = { ...this.contact?.additional_attributes };
        additAttribUpdated.extraAdds = { cpf: { value: this.cpf } };
        try {
          await this.$store.dispatch('contacts/update', {
            id: this.contact?.id,
            additional_attributes: additAttribUpdated,
          });
        } catch (e) {
          useAlert(this.$t('CONVERSATION.RESOLUTION_MODAL.CPF.API_ERROR'));
          this.updateCpfError = true;
        }
      }
    },
    async resolveConversation() {
      // If setLabel is not mandatory, we resolve conversation same with errors in the req
      const labelMandatoryAndError =
        this.setLabelIsMandatory && this.setLabelError;
      const cpfMandatoryAndError = this.cpfIsMandatory && this.updateCpfError;
      if (cpfMandatoryAndError || labelMandatoryAndError) return;
      try {
        await this.$store.dispatch('resolveWithReason', {
          conversationId: this.conversation.id,
          resolutionReason: this.selectedResolutionReason,
        });
        useAlert(this.$t('CONVERSATION.RESOLUTION_MODAL.API_SUCCESS'));
        this.onClose();
      } catch (e) {
        useAlert(this.$t('CONVERSATION.RESOLUTION_MODAL.API_ERROR'));
      }
    },
  },
};
</script>

<template>
  <div class="column content-box">
    <woot-modal-header
      :header-title="$t('CONVERSATION.RESOLUTION_MODAL.HEADER')"
      class="mb-4"
    />
    <div class="main-container">
      <!-- START SELECT RESOLUTION OPTION -->
      <InputRadioGroup
        name="widget-screen"
        :items="resolutionOptions"
        @on-change-resolution-option="onChangeResolutionOption"
      />
      <!-- END SELECT RESOLUTION OPTION -->

      <!-- START SELECT RESOLUTION REASON OPTION -->
      <InputRadioGroup
        name="widget-screen"
        :items="resolutionReasons"
        column-style
        @on-change-resolution-option="onChangeResolutionReason"
      />
      <!-- END SELECT RESOLUTION OPTION -->

      <!-- START ADD LABEL SECTION -->
      <label
        class="multiselect-wrap--small w-[90%]"
        :class="{ error: v$.selectedLabels.$error }"
      >
        {{ $t('CONVERSATION.RESOLUTION_MODAL.SET_LABEL.HEADER') }}
        <multiselect
          v-model="selectedLabels"
          :options="audienceList"
          track-by="id"
          label="title"
          multiple
          :close-on-select="false"
          :clear-on-select="false"
          hide-selected
          :placeholder="
            $t('CONVERSATION.RESOLUTION_MODAL.SET_LABEL.PLACEHOLDER')
          "
          selected-label
          :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
          :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
          @blur="v$.selectedLabels.$touch"
          @select="v$.selectedLabels.$touch"
        />
        <span v-if="v$.selectedLabels.$error" class="editor-warning__message">
          {{ $t('CONVERSATION.RESOLUTION_MODAL.SET_LABEL.ERROR') }}
        </span>
      </label>
      <!-- END ADD LABEL SECTION -->

      <!-- START ADD CPF TO CONTACT SECTION -->
      <div v-if="cpfIsMandatory" class="w-[90%]">
        <label :class="{ error: v$.cpf.$error }">
          {{ $t('CONVERSATION.RESOLUTION_MODAL.CPF.HEADER') }}
          <input
            v-model.trim="cpf"
            v-mask="'###.###.###-##'"
            type="text"
            :placeholder="$t('CONTACT_FORM.FORM.CPF.PLACEHOLDER')"
            @input="v$.cpf.$touch"
          />
          <span v-if="!v$.cpf.isValidCPF" class="message">
            {{ $t('CONTACT_FORM.FORM.CPF.ERROR') }}
          </span>
        </label>
      </div>
      <!-- END ADD CPF TO CONTACT SECTION -->

      <woot-button
        class-names="resolve"
        color-scheme="success"
        icon="checkmark"
        emoji="✅"
        :is-loading="isLoading"
        class="mb-8"
        :disabled="resolveButtonDisabled"
        @click="resolveConversationWithReason"
      >
        {{ $t('CONVERSATION.HEADER.RESOLVE_ACTION') }}
      </woot-button>
    </div>
  </div>
</template>
